import { CctvStatusEnum, PermissionEnum } from '@api/enum';
import { ICamera, ICameraCCTV } from '@api/models';
import useAuthPermission from '@hooks/use-auth-permission';
import { EllipsisHorizontalIcon, LocationIcon } from '@icon/index';
import { Dropdown, DropdownItem, DropdownList, DropdownTrigger } from '@library/dropdown';
import { GalleryCard, Img } from 'modules/shared/features/content';
import moment from 'moment';
import { useMemo } from 'react';
import CctvStatusBadge from '../CctvStatusBadge/CctvStatusBadge';
import useCctvStatus from 'modules/cctv-cameras/hooks/useCctvStatus';
import useCctvStatusChecker from 'modules/cctv-cameras/hooks/useCctvStatusChecker';

interface ICCtvCameraCard {
    cctvCamera: ICameraCCTV;
    onDeleteClick: (camera: ICameraCCTV) => void;
    onLocationClick?: () => void;
    onEditClick: () => void;
    hasDeletePermission: boolean;
    clientId?: string;
    projectId?: string;
}

const CctvCameraCard = (props: ICCtvCameraCard) => {
    const { cctvCamera, onEditClick, hasDeletePermission, onDeleteClick, onLocationClick, clientId, projectId } = props;
    const { hasPermission } = useAuthPermission();
    const isOlderThan3Days = (date: string) => {
        return moment().diff(moment(date, 'YYYYMMDD'), 'days') <= 3;
    };
    const { isLoading, isOffline, isOnline, status } = useCctvStatusChecker({
        hlsUrl: cctvCamera?.defaultChannel?.hlsUrl,
        webrtcUrl: cctvCamera?.defaultChannel?.webrtcUrl,
        clientId,
        projectId,
        cameraId: cctvCamera?._id,
    });

    return (
        <GalleryCard>
            <GalleryCard.ActionBar className="absolute top-0 right-0 opacity-100 visible pointer-events-auto z-50">
                {cctvCamera.location.latitude && cctvCamera.location.longitude && (
                    <div className="w-6 h-6 grid place-items-center rounded-[4px] bg-floating-color image-overlay-button-shadow hover-transition ">
                        <LocationIcon className="text-grayscale-600" onClick={onLocationClick} />
                    </div>
                )}
                {hasPermission(PermissionEnum.LIVE_CAMERA_UPDATE) && (
                    <div>
                        <Dropdown placement="bottom-end">
                            <DropdownTrigger className="w-6 h-6 grid place-items-center rounded-[4px] bg-floating-color image-overlay-button-shadow hover-transition hover:brightness-90">
                                <EllipsisHorizontalIcon className="text-grayscale-600" />
                            </DropdownTrigger>

                            <DropdownList className="min-w-[100px]">
                                <DropdownItem onClick={onEditClick}>Edit</DropdownItem>
                                {hasPermission && (
                                    <DropdownItem onClick={() => onDeleteClick(cctvCamera)} className="text-danger">
                                        Delete
                                    </DropdownItem>
                                )}
                            </DropdownList>
                        </Dropdown>
                    </div>
                )}
            </GalleryCard.ActionBar>

            {/* <GalleryCard.Media>
                {isOffline ? (
                    <div className=" top-0 left-0 w-full h-full bg-black/[35%] rounded-lg z-[3]">
                        <div className="flex items-center justify-center h-full w-full text-white font-medium text-xs text-center">
                            <div>
                                Last active
                                <div className="font-semibold mt-[6px]">
                                    {cctvCamera.lastUpdated
                                        ? moment(cctvCamera?.installationDate, 'YYYYMMDD').format('DD MMM, YYYY')
                                        : 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <Img
                        className={`w-full aspect-image rounded-lg object-cover object-center `}
                        src={cctvCamera.latestImage?.url}
                    />
                )}
            </GalleryCard.Media> */}
            <GalleryCard.Media className="relative">
                <Img
                    className="w-full aspect-image rounded-lg object-cover object-center"
                    src={cctvCamera.latestImage?.url}
                />

                {isOffline && (
                    <div className="absolute top-0 left-0 w-full h-full bg-black/[35%] rounded-lg  border-4 border-danger-500">
                        <div className="flex items-center justify-center h-full w-full text-white font-medium text-xs text-center">
                            <div>
                                Last active
                                <div className="font-semibold mt-[6px]">
                                    {cctvCamera.latestImage
                                        ? moment
                                              .tz(cctvCamera.lastUpdated, cctvCamera.timezone)
                                              .format('h:mm a [·] DD MMM, YYYY')
                                        : 'Not available'}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </GalleryCard.Media>

            <GalleryCard.Title>{cctvCamera.name}</GalleryCard.Title>

            <GalleryCard.Footer>
                <div className="flex flex-wrap gap-4 justify-between">
                    <GalleryCard.FooterItem
                        label="Camera Status"
                        content={
                            // <div className={`${statusOptions.textClass}`}>{statusOptions.label}</div>
                            <CctvStatusBadge status={status} />
                        }
                    ></GalleryCard.FooterItem>

                    <GalleryCard.FooterItem
                        label="Installed On"
                        content={moment(cctvCamera?.installationDate, 'YYYYMMDD').format('DD MMM, YYYY')}
                    ></GalleryCard.FooterItem>
                </div>
            </GalleryCard.Footer>
        </GalleryCard>
    );
};

export default CctvCameraCard;
